import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import { ListItemButton, Typography } from "@mui/material";
import { useState } from "react";
import { useOrgan } from "../../../../hooks/useOrgan";
import { organizationPrimaryColors } from "../../../../layouts/PermanentSidebarMUI";

// Funções de manipulação de itens
function not(a, b) {
  return a.filter((value) => b.indexOf(value) === -1);
}

function intersection(a, b) {
  return a.filter((value) => b.indexOf(value) !== -1);
}

function union(a, b) {
  return [...a, ...not(b, a)];
}

const MovementsShipmentsTransferList = ({
  leftItems,
  rightItems,
  onLeftItemsChange,
  onRightItemsChange,
  titleLeft,
  titleRight,
}) => {
  const { selectedOrgan } = useOrgan();
  const primaryColor = selectedOrgan?.name
    ? organizationPrimaryColors[selectedOrgan.name.toUpperCase()] ||
      organizationPrimaryColors.ALUCOM
    : organizationPrimaryColors.ALUCOM;

  const [checked, setChecked] = useState([]);

  const leftChecked = intersection(checked, leftItems);
  const rightChecked = intersection(checked, rightItems);

  const handleToggle = (value) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const numberOfChecked = (items) => intersection(checked, items).length;

  const handleCheckedLeft = () => {
    onLeftItemsChange(union(leftItems, rightChecked)); // Mover da direita para a esquerda
    onRightItemsChange(not(rightItems, rightChecked)); // Remover da direita
    setChecked(not(checked, rightChecked)); // Atualizar os itens selecionados
  };

  const handleCheckedRight = () => {
    onRightItemsChange(union(rightItems, leftChecked)); // Mover da esquerda para a direita
    onLeftItemsChange(not(leftItems, leftChecked)); // Remover da esquerda
    setChecked(not(checked, leftChecked)); // Atualizar os itens selecionados
  };

  const handleMoveAllLeft = () => {
    onLeftItemsChange(union(leftItems, rightItems)); // Mover todos da direita para a esquerda
    onRightItemsChange([]); // Limpar a lista da direita
    setChecked([]); // Limpar os itens selecionados
  };

  const handleMoveAllRight = () => {
    onRightItemsChange(union(rightItems, leftItems)); // Mover todos da esquerda para a direita
    onLeftItemsChange([]); // Limpar a lista da esquerda
    setChecked([]); // Limpar os itens selecionados
  };

  const handleToggleAll = (items) => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(not(checked, items));
    } else {
      setChecked(union(checked, items));
    }
  };

  const customList = (title, items, left = true) => (
    <Card
      elevation={3}
      sx={{
        borderRadius: "4px",
        overflow: "hidden",
        minWidth: "100%",
        width: "100%",
        flexShrink: 0,
        bgcolor: "#f8f8f8",
        border: "1px solid #e0e0e0",
        boxShadow: "0px 1px 5px rgba(0, 0, 0, 0.1)",
      }}>
      <CardHeader
        sx={{
          backgroundColor: primaryColor,
          px: 2,
          py: 1,
          color: "white",
        }}
        avatar={
          <Checkbox
            onClick={handleToggleAll(items)}
            checked={
              numberOfChecked(items) === items.length && items.length !== 0
            }
            indeterminate={
              numberOfChecked(items) !== items.length &&
              numberOfChecked(items) !== 0
            }
            disabled={items.length === 0}
            slotProps={{
              "aria-label": "all items selected",
            }}
            sx={{ color: "white" }}
          />
        }
        title={
          <Typography
            variant="h6"
            sx={{
              fontSize: "16px",
              fontWeight: "bold",
            }}>
            {title}
          </Typography>
        }
        subheader={
          <Typography
            variant="h6"
            sx={{ fontSize: "14px", fontWeight: "normal", color: "white" }}>
            {numberOfChecked(items)}/{items.length} selecionados
          </Typography>
        }
      />

      <List
        sx={{
          width: "100%",
          maxHeight: 225,
          overflow: "auto",
          padding: 0,
        }}
        dense
        component="div"
        role="list">
        {items.map((value, index) => {
          const labelId = `transfer-list-item-${value.value}-label`;
          return (
            <ListItemButton
              key={value.uuid + index}
              role="listitem"
              onClick={handleToggle(value)}
              sx={{
                backgroundColor: left ? "#E2E3E5" : "#D4EDDA",
                "&:hover": {
                  backgroundColor: left
                    ? index % 1 === 0
                      ? "#ffffff"
                      : "#E2E3E5"
                    : index % 1 === 0
                    ? "#ffffff"
                    : "#D4EDDA",
                },
                color: left ? "#484541" : "#155724",
                py: 0.5,
                px: 1.5,
              }}>
              <ListItemIcon sx={{ minWidth: "32px" }}>
                <Checkbox
                  checked={checked.indexOf(value) !== -1}
                  tabIndex={-1}
                  disableRipple
                  slotProps={{ "aria-labelledby": labelId }}
                />
              </ListItemIcon>
              <ListItemText id={labelId} primary={value.label} />
            </ListItemButton>
          );
        })}
      </List>
    </Card>
  );

  return (
    <Grid
      container
      wrap="nowrap"
      spacing={1}
      justifyContent="space-between"
      alignItems="stretch"
      sx={{
        mb: 2,
        mt: 1,
        overflowX: "auto", // permite scroll se não couber
      }}>
      <Grid sx={{ width: "49%" }} item>
        {customList(titleLeft, leftItems, true)}
      </Grid>
      <Grid item alignContent="center" sx={{ flexShrink: 0 }}>
        <Grid container direction="column">
          <Button
            sx={{ my: 0.5 }}
            variant="contained"
            size="small"
            onClick={handleMoveAllRight}
            disabled={leftItems.length === 0}
            aria-label="move all right">
            &gt;&gt;
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="contained"
            size="small"
            onClick={handleCheckedRight}
            disabled={leftChecked.length === 0}
            aria-label="move selected right">
            &gt;
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="contained"
            size="small"
            onClick={handleCheckedLeft}
            disabled={rightChecked.length === 0}
            aria-label="move selected left">
            &lt;
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="contained"
            size="small"
            onClick={handleMoveAllLeft}
            disabled={rightItems.length === 0}
            aria-label="move all left">
            &lt;&lt;
          </Button>
        </Grid>
      </Grid>
      <Grid item sx={{ width: "49%" }}>
        {customList(titleRight, rightItems, false)}
      </Grid>
    </Grid>
  );
};

export default MovementsShipmentsTransferList;
